import type { NavigationGuard } from 'vue-router'
import { inject } from 'vue'
import { errorHandlerKey, servicesKey } from '@/config/keys.ts'
import { routeNames } from '@/router/names.ts'
import { onApiErrorHandler } from '@/helpers/backendErrors.ts'
import { useToast } from 'primevue'
import i18n from '@/i18n'
import { useAccountStore } from '@/stores/account.ts'
import { routeNamesByDocumentCode } from '@/config/requiredDocumentsCodes.ts'
import { useSubAccountStore } from '@/stores/subAccount.ts'

export const isChildProtectAcceptedGuard: NavigationGuard = async (to, from, next) => {
    const services = inject(servicesKey)
    const errorHandler = inject(errorHandlerKey)
    const toast = useToast()
    const accountStore = useAccountStore()
    const subAccountStore = useSubAccountStore()

    try {
        const { childProtectAccepted } = await services!.account.getIsChildProtectAccepted()

        if (!childProtectAccepted) {
            return next()
        }

        toast.add({
            severity: 'success',
            summary: i18n.global.t('The document has already been approved'),
            life: 4000,
        })

        if (!subAccountStore.subAccountsOptions.length) {
            const { isSuccess, documentToAccept } = await subAccountStore.loadSubAccountsOptions()

            if (documentToAccept) {
                return next({
                    name: routeNamesByDocumentCode[documentToAccept],
                })
            }

            if (!isSuccess) {
                throw new Error('Cannot load sub accounts options in isChildProtectAcceptedGuard')
            }
        }

        return next({
            name: routeNames.HOME_VIEW_NAME,
            params: {
                subAccountId: subAccountStore.subAccountsOptions[0]!.subaccountId,
            },
        })
    } catch (err) {
        onApiErrorHandler({
            err,
            toast,
            errorTitle: i18n.global.t('Error'),
            defaultErrorMessage: i18n.global.t(
                'There was a problem retrieving your account details',
            ),
            action: 'isChildProtectAcceptedGuard',
            errorHandler,
        })

        accountStore.logout(true)

        return next({
            name: routeNames.LOGIN_VIEW_NAME,
        })
    }
}
